import isEmpty from 'lodash-es/isEmpty';
import isNil from 'lodash-es/isNil';
import { Resource } from '../Resource';
import * as methods from '../methods';
import * as transformers from '../transformers';
import { BillingStatus } from './../enums';
import { plotMutationExternalInvalidationKeys } from './plots';
const rootPath = 'organizations';
const fromRaw = ({ id, name, created_at, my_role, owner, network, active_options, billing_status, invoiced_organization, subscribed_network, meta, virtual_devices_count, virtual_devices_quota, odoo_name, address_city, address_line_1, membership_code, address_line_2, address_zip, country, siret, customer, headquarter, trial, parent_network }) => ({
    id,
    name,
    companyName: odoo_name,
    siret,
    addressLine1: address_line_1,
    addressLine2: address_line_2,
    zipCode: address_zip,
    ...(customer ? { customer: fromRawCustomerInfos(customer) } : {}),
    city: address_city,
    countryCode: country,
    createdAt: created_at,
    myRole: my_role,
    ownerFullName: owner,
    membershipCode: membership_code,
    networkType: network,
    activeOptions: active_options,
    billingStatus: billing_status,
    invoicedOrganizationId: invoiced_organization,
    subscribedNetworkId: subscribed_network,
    crops: meta?.main_crops,
    virtualDevicesCount: virtual_devices_count,
    virtualDevicesQuota: virtual_devices_quota,
    optionWBArvalisMonoCropCrop: isNil(meta) || isNil(meta['OAD-BH-ARVALIS-MONO'])
        ? undefined
        : meta['OAD-BH-ARVALIS-MONO'].crop,
    headquarter: isNil(headquarter)
        ? null
        : {
            name: headquarter.name,
            lat: headquarter.location.coordinates[1],
            lng: headquarter.location.coordinates[0]
        },
    trial,
    network: parent_network
});
const fromRawCustomerInfos = ({ siret, address_city: city, address_line_1: addressLine1, address_line_2: addressLine2, address_zip: zipCode, country, name, code: membershipCode, id, organization: orgId, billing_status: billingStatus }) => ({
    name,
    siret,
    addressLine1,
    addressLine2,
    zipCode,
    city,
    countryCode: isEmpty(country) ? null : country,
    membershipCode,
    id,
    orgId,
    billingStatus
});
const toRawCustomerInfos = ({ name, siret, addressLine1, addressLine2, zipCode, city, countryCode, membershipCode, billingStatus }) => ({
    siret,
    address_city: city,
    address_line_1: addressLine1,
    address_line_2: addressLine2,
    address_zip: zipCode,
    country: countryCode,
    name,
    code: membershipCode,
    billing_status: billingStatus
});
const toRaw = ({ name, crops, companyName, siret, addressLine1, addressLine2, zipCode, city, countryCode, customer, headquarter }) => ({
    ...(!isNil(name) ? { name } : {}),
    ...(!isNil(customer) ? { customer: toRawCustomerInfos(customer) } : {}),
    ...(crops ? { meta: { main_crops: crops } } : {}),
    ...(!isNil(companyName) ? { company_name: companyName } : {}),
    ...(!isNil(city) ? { address_city: city } : {}),
    ...(!isNil(addressLine1) ? { address_line_1: addressLine1 } : {}),
    ...(!isNil(addressLine2) ? { address_line_2: addressLine2 } : {}),
    ...(!isNil(zipCode) ? { address_zip: zipCode } : {}),
    ...(!isNil(countryCode) ? { address_city: countryCode } : {}),
    ...(!isNil(siret) ? { siret } : {}),
    ...(!isNil(headquarter)
        ? {
            headquarter: {
                name: headquarter.name,
                lat: headquarter.lat,
                lng: headquarter.lng
            }
        }
        : {})
});
/** Premium mean plus or expert plan (billing status) */
export const isOrgPremium = (org) => org?.billingStatus === BillingStatus.plus || org?.billingStatus === BillingStatus.expert;
const orgs = Resource((requesterBuilder) => {
    const getMany = (query) => requesterBuilder({
        path: rootPath,
        requestCtx: {
            transform: transformers.paginatedResponse(fromRaw),
            params: {
                query: {
                    limit: query?.limit,
                    offset: query?.offset,
                    by_role: query?.myRole,
                    id__in: query?.ids
                }
            }
        }
    });
    const getAllPages = methods.getAllPages({ getMany });
    const get = methods.get({
        requesterBuilder,
        rootPath,
        fromRaw,
        cacheConfig: {
            staleTime: 60 * 1000
        }
    });
    const createGet = (id) => {
        plotMutationExternalInvalidationKeys.add(get(id).invalidationKey[0]);
        return get(id);
    };
    const buildKeysToInvalidate = (id) => [
        getMany().invalidationKey,
        getAllPages().invalidationKey,
        get(id).invalidationKey
    ];
    return {
        leave: (orgId) => methods.create({
            requesterBuilder,
            rootPath: `${rootPath}/${orgId}/leave`,
            fromRaw: () => undefined,
            toRaw: () => ({}),
            buildKeysToInvalidate
        })(),
        /** Update either an org or a network org */
        update: methods.update({
            requesterBuilder,
            rootPath,
            fromRaw,
            toRaw,
            method: 'PATCH',
            buildKeysToInvalidate
        }),
        getMany,
        get: createGet,
        getAllPages
    };
});
export default orgs;
